class SessionTimeoutService {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private lastActivityTime: number = Date.now();
  private readonly TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes
  private readonly WARNING_DURATION = 5 * 60 * 1000; // 5 minutes before timeout
  private onTimeout: (() => void) | null = null;
  private onWarning: (() => void) | null = null;
  private onWarningClear: (() => void) | null = null;

  constructor() {
    this.bindActivityListeners();
  }

  public setCallbacks(callbacks: {
    onTimeout: () => void;
    onWarning: () => void;
    onWarningClear: () => void;
  }) {
    this.onTimeout = callbacks.onTimeout;
    this.onWarning = callbacks.onWarning;
    this.onWarningClear = callbacks.onWarningClear;
  }

  public startSession() {
    this.lastActivityTime = Date.now();
    this.resetTimeout();
  }

  public extendSession() {
    this.lastActivityTime = Date.now();
    this.resetTimeout();
    this.onWarningClear?.();
  }

  public endSession() {
    this.clearTimeouts();
    this.removeActivityListeners();
  }

  public getTimeRemaining(): number {
    const elapsed = Date.now() - this.lastActivityTime;
    return Math.max(0, this.TIMEOUT_DURATION - elapsed);
  }

  public getWarningTimeRemaining(): number {
    const timeRemaining = this.getTimeRemaining();
    return Math.max(0, timeRemaining - this.WARNING_DURATION);
  }

  public isInWarningPeriod(): boolean {
    return (
      this.getTimeRemaining() <= this.WARNING_DURATION &&
      this.getTimeRemaining() > 0
    );
  }

  private resetTimeout() {
    this.clearTimeouts();

    // Set warning timeout
    const warningTime = this.TIMEOUT_DURATION - this.WARNING_DURATION;
    this.warningTimeoutId = setTimeout(() => {
      this.onWarning?.();
    }, warningTime);

    // Set session timeout
    this.timeoutId = setTimeout(() => {
      this.onTimeout?.();
    }, this.TIMEOUT_DURATION);
  }

  private clearTimeouts() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  private handleActivity = () => {
    // Only extend if not in warning period to prevent accidental extensions
    if (!this.isInWarningPeriod()) {
      this.extendSession();
    }
  };

  private bindActivityListeners() {
    // Track various user activities
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, this.handleActivity, true);
    });
  }

  private removeActivityListeners() {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }
}

export default new SessionTimeoutService();
